import FVector from '@data/geometry/FVector';
import { CameraMode } from '@visualizer/types';
import { AxiosResponse } from 'axios';
import { UUID } from 'crypto';
import { requireApp } from 'store/assertions';

const getApiClient = () => requireApp().apiClient;
const getProjectId = () => requireApp().projectId;

const buildProjectPath = (suffix = ''): string => {
    const projectId = getProjectId();

    if (!projectId) {
        throw new Error('Project id is missing. Authenticate before managing cameras.');
    }

    return `projects/${projectId}/cameras${suffix}`;
};

interface CameraDtoBase {
    id: UUID;
    name: string;
    type: CameraMode;
    projectId: UUID;
    createdAt: Date;
}

interface CameraVectorDto {
    x: number;
    y: number;
    z: number;
}

type CameraDtoResponse = CameraDtoBase & {
    position: CameraVectorDto;
    target: CameraVectorDto;
};

export interface CameraDto extends CameraDtoBase {
    position: FVector;
    target: FVector;
}

export interface CreateCameraPayload {
    name: string;
    type: CameraMode;
    position: FVector;
    target: FVector;
}

export interface UpdateCameraPayload {
    name?: string;
    type?: CameraMode;
    position?: FVector;
    target?: FVector;
}

export type CameraId = string;

export async function listCameras(): Promise<CameraDto[]> {
    const response: AxiosResponse<CameraDtoResponse[]> = await getApiClient().get(buildProjectPath());
    return response.data.map(mapCameraDto);
}

const castVector = ({ x, y, z }: CameraVectorDto): FVector => new FVector(x, y, z);

const mapCameraDto = (camera: CameraDtoResponse): CameraDto => ({
    ...camera,
    position: castVector(camera.position),
    target: castVector(camera.target),
});

export async function getCamera(cameraId: CameraId): Promise<CameraDto> {
    const response: AxiosResponse<CameraDtoResponse> = await getApiClient().get(buildProjectPath(`/${cameraId}`));
    return mapCameraDto(response.data);
}

export async function createCamera(payload: CreateCameraPayload): Promise<CameraDto> {
    const response: AxiosResponse<CameraDtoResponse> = await getApiClient().post(buildProjectPath(), payload);
    return mapCameraDto(response.data);
}

export async function updateCamera(cameraId: CameraId, payload: UpdateCameraPayload): Promise<CameraDto> {
    const response: AxiosResponse<CameraDtoResponse> = await getApiClient().patch(buildProjectPath(`/${cameraId}`), payload);
    return mapCameraDto(response.data);
}

export async function deleteCamera(cameraId: CameraId): Promise<void> {
    await getApiClient().delete(buildProjectPath(`/${cameraId}`));
}
